import presenceReportHours from './presenceReportHours'
import toMillis from '../measurementSignals/toMillis'

/**
 * Vouwt de aanwezigheidsrapporten van een heel jaar samen tot één rij per
 * deelnemer, met twaalf maandtotalen en een jaartotaal.
 *
 * De maandweergave toont een raster van deelnemers × dagen en past daarmee
 * precies één maand op het scherm. Voor de vraag 'hoe stond deze deelnemer er
 * dit jaar voor' moet je dan twaalf keer van maand wisselen en zelf optellen.
 * Deze helper levert het jaar in één keer.
 *
 * Per maand wordt niet alleen het aantal uren bijgehouden maar ook hóéveel
 * rapporten eraan ten grondslag liggen. Zonder dat aantal is 'geen enkele
 * registratie' niet van 'de hele maand ziek gemeld' te onderscheiden — allebei
 * nul uur — en dat is precies het verschil waar de gebruiker naar kijkt.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */

const MONTHS_IN_YEAR = 12

/**
 * @typedef {object} PresenceYearMonth
 * @property {number} hours - Aanwezige uren in deze maand
 * @property {number} count - Aantal rapporten in deze maand (0 = niets geregistreerd)
 * @property {number} absent - Hoeveel daarvan een afwezigheid waren
 * @property {number} corrections - Hoeveel daarvan een correctie waren
 */

/**
 * @typedef {object} PresenceYearRow
 * @property {string} ID - `Personas.ID`
 * @property {string} name - Naam van de deelnemer
 * @property {string} status - `Personas.Status` ('active', 'inactive', …)
 * @property {Array<PresenceYearMonth>} months - Twaalf maanden, januari eerst
 * @property {number} total - Jaartotaal in uren
 * @property {number} count - Aantal rapporten in het hele jaar
 * @property {object} persona - Het oorspronkelijke deelnemersdocument
 */

/** @returns {PresenceYearMonth} */
const emptyMonth = () => ({ hours: 0, count: 0, absent: 0, corrections: 0 })

/**
 * @param {Array<object>|null|undefined} personas - Deelnemers die een rij krijgen
 * @param {Array<object>|null|undefined} reports - Rapporten uit `PersonaPresenceReports`
 * @param {number} year - Het jaar waarover geteld wordt
 * @returns {Array<PresenceYearRow>}
 */
const buildPresenceYearRows = (personas, reports, year) => {
    const personaList = Array.isArray(personas) ? personas : []
    const reportList = Array.isArray(reports) ? reports : []

    // Elke deelnemer krijgt een rij, óók zonder één registratie: een lege rij
    // is het antwoord op 'is er van deze persoon iets vastgelegd?' en dat
    // antwoord verdwijnt als de rij zelf verdwijnt.
    const rows = personaList.map((persona) => ({
        ID: persona?.ID,
        name: persona?.Name || '',
        status: persona?.Status || '',
        months: Array.from({ length: MONTHS_IN_YEAR }, emptyMonth),
        total: 0,
        count: 0,
        persona,
    }))

    const rowByPersonaId = new Map()
    for (const row of rows) {
        if (row.ID) rowByPersonaId.set(row.ID, row)
    }

    for (const report of reportList) {
        const row = rowByPersonaId.get(report?.PersonaID)
        if (!row) continue

        const millis = toMillis(report?.Timestamp)
        if (millis === null) continue

        const date = new Date(millis)
        if (date.getFullYear() !== year) continue

        const { hours, type } = presenceReportHours(report)
        const month = row.months[date.getMonth()]

        month.hours += hours
        month.count += 1
        if (type === 'absent') month.absent += 1
        if (type === 'correction') month.corrections += 1

        row.total += hours
        row.count += 1
    }

    return rows
}

export default buildPresenceYearRows
export { MONTHS_IN_YEAR }
